import React from 'react';
import { X, AlertCircle } from 'lucide-react';

/**
 * Error modal component
 * Displays error messages in a user-friendly dialog
 */
const ErrorModal = ({ 
  title = 'Error', 
  message = 'Something went wrong',
  error = null,
  onClose,
  actionLabel = 'Close',
  onAction,
  isDangerous = false 
}) => {
  const handleAction = () => {
    if (onAction) {
      onAction();
    }
    if (onClose) {
      onClose();
    }
  };

  // Format error message
  const getErrorMessage = () => {
    if (error?.message) return error.message;
    if (typeof error === 'string') return error;
    return message;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-neutral-800 rounded-xl max-w-md w-full p-6 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${
              isDangerous 
                ? 'bg-red-100 dark:bg-red-900/30' 
                : 'bg-amber-100 dark:bg-amber-900/30'
            }`}>
              <AlertCircle 
                size={24} 
                className={isDangerous ? 'text-red-600' : 'text-amber-600'} 
              />
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {title}
            </h2>
          </div>
          <button
            onClick={() => onClose?.()}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Message */}
        <div className="mb-6">
          <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
            {getErrorMessage()}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => onClose?.()}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-neutral-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors font-medium text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleAction}
            className={`flex-1 px-4 py-2 rounded-lg text-white font-medium text-sm transition-colors ${
              isDangerous
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorModal;
